import { useState } from "react";
import { toast } from "sonner";
import { Loader2, MailCheck, LogOut } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { useMember } from "@/context/MemberContext";
import { Input } from "@/components/ui/input";

export const MemberSignIn = ({ electorate }) => {
  const { member, signOut } = useMember();
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  if (member) {
    return (
      <div data-testid="member-signed-in" className="flex flex-wrap items-center justify-between gap-3 rounded-xl bg-[#E5EDE3] border border-[#8FA58A]/40 px-4 py-3 text-sm">
        <span>
          Signed in as <strong>{member.name}</strong> · F.E.R.M for {member.electorate}
          {electorate && member.electorate !== electorate && <span className="text-[#E2705A]"> (you can only post to {member.electorate})</span>}
        </span>
        <button onClick={signOut} data-testid="member-sign-out" className="inline-flex items-center gap-1 text-xs text-[#5A7355] hover:text-[#E2705A]"><LogOut className="w-3 h-3" /> Sign out</button>
      </div>
    );
  }

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/member/request-link", { email, electorate });
      setSent(true);
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  if (sent) {
    return (
      <div data-testid="member-link-sent" className="rounded-xl bg-[#E5EDE3] border border-[#8FA58A]/40 px-4 py-4 text-sm flex gap-3 items-start">
        <MailCheck className="w-5 h-5 text-[#5A7355] flex-shrink-0 mt-0.5" />
        <p>If <strong>{email}</strong> is registered as a F.E.R.M, a one-time sign-in link is on its way. It expires in 30 minutes. <button className="text-[#E2705A] hover:underline" onClick={() => setSent(false)} data-testid="member-link-retry">Use a different email</button></p>
      </div>
    );
  }

  return (
    <form onSubmit={submit} data-testid="member-sign-in-form" className="rounded-xl bg-[#FAF8F2] border border-[#D8D2C4] p-4">
      <p className="text-sm font-semibold text-[#1B2A41]">{electorate ? `F.E.R.Ms of ${electorate}: sign in to log a bill or vote` : "Registered F.E.R.M? Sign in"}</p>
      <p className="text-xs text-[#5C6A79] mt-1">Enter the email you registered with. We'll email you a one-time link — no password needed.</p>
      <div className="flex gap-2 mt-3">
        <Input data-testid="member-email-input" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" className="h-11 bg-white border-[#D8D2C4]" />
        <button type="submit" disabled={busy} data-testid="member-request-link-button" className="h-11 px-5 rounded-full bg-[#1B2A41] hover:bg-[#121E2B] text-[#FDFBF7] font-type font-bold uppercase tracking-wider text-xs inline-flex items-center gap-2 disabled:opacity-60 whitespace-nowrap">
          {busy && <Loader2 className="w-4 h-4 animate-spin" />} Email me a link
        </button>
      </div>
    </form>
  );
};
